import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { MessageTemplateInput } from '@/lib/types';

export const templateKeys = {
  all: ['templates'] as const,
  job: (jobId: string) => ['templates', 'job', jobId] as const,
  org: ['templates', 'org'] as const,
};

/** Templates available to a job: its own plus the organization-wide ones. */
export function useJobTemplates(jobId: string, enabled = true) {
  return useQuery({ queryKey: templateKeys.job(jobId), queryFn: () => api.templates.listForJob(jobId), enabled: enabled && !!jobId, staleTime: 30_000 });
}

export function useOrgTemplates() {
  return useQuery({ queryKey: templateKeys.org, queryFn: () => api.templates.org.list(), staleTime: 30_000 });
}

/** Create / update / delete for one scope; every template query is refreshed afterwards. */
export function useTemplateMutations(scope: { jobId: string } | { org: true }) {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: templateKeys.all });
  const create = useMutation({ mutationFn: (body: MessageTemplateInput) => ('jobId' in scope ? api.templates.create(scope.jobId, body) : api.templates.org.create(body)), onSuccess: done });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<MessageTemplateInput> }) => ('jobId' in scope ? api.templates.update(scope.jobId, id, body) : api.templates.org.update(id, body)),
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: (id: string) => ('jobId' in scope ? api.templates.remove(scope.jobId, id) : api.templates.org.remove(id)), onSuccess: done });
  return { create, update, remove };
}

export function useMarkTemplateUsed(jobId: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.templates.use(jobId, id), onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }) });
}
